import React, { useState, useEffect, useRef } from 'react';
import { Search, X, ShoppingBag, Send, Loader2, Sparkles, Check } from 'lucide-react';
import { getProducts } from '../api/client';
import { isRTL } from '../utils/translations';

const ProductShareModal = ({ isOpen, onClose, onShareProduct }) => {
    const rtl = isRTL();
    const [search, setSearch] = useState('');
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sharedId, setSharedId] = useState(null);
    const debounceTimerRef = useRef(null);

    const fetchItems = async (searchQuery = '') => {
        setLoading(true);
        try {
            const res = await getProducts(0, 24, searchQuery);
            const list = res?.data || (Array.isArray(res) ? res : []);
            setProducts(list);
        } catch (err) {
            console.error('Failed to load products for share:', err);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        fetchItems('');
    }, [isOpen]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            fetchItems(val);
        }, 300);
    };

    const handleShare = (product) => {
        setSharedId(product.id);
        const imageUrl = product.images?.[0] || product.image || product.thumbnailUrl || '';
        const payload = {
            id: product.id,
            name: product.name || product.title,
            price: product.price,
            currency: product.currency || '$',
            image: imageUrl
        };
        onShareProduct(payload);
        setTimeout(() => {
            setSharedId(null);
            onClose();
        }, 350);
    };

    if (!isOpen) return null;

    return (
        <div 
            className="chat-modal-overlay"
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                backdropFilter: 'blur(8px)',
                zIndex: 100050,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px'
            }}
            onClick={onClose}
        >
            <div
                className="chat-modal-card"
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: '100%',
                    maxWidth: '480px',
                    maxHeight: '85vh',
                    background: '#0B141A',
                    border: '1px solid rgba(212, 175, 55, 0.35)',
                    borderRadius: '20px',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8), 0 0 30px rgba(212, 175, 55, 0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    direction: rtl ? 'rtl' : 'ltr'
                }}
            >
                {/* Modal Header */}
                <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'linear-gradient(180deg, #16222b 0%, #0B141A 100%)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '10px',
                            background: 'rgba(212, 175, 55, 0.15)',
                            border: '1px solid rgba(212, 175, 55, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <ShoppingBag size={20} color="#D4AF37" />
                        </div>
                        <div>
                            <h3 style={{ margin: 0, color: '#FFFFFF', fontSize: '15px', fontWeight: '800' }}>
                                {rtl ? 'مشاركة منتج في المحادثة' : 'Share Product in Chat'}
                            </h3>
                            <span style={{ fontSize: '11px', color: '#94A3B8' }}>
                                {rtl ? 'اختر منتجاً فاخراً لإرساله مباشرة في الرسالة' : 'Select an exclusive item to share with peer'}
                            </span>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: 'rgba(255, 255, 255, 0.06)',
                            border: 'none',
                            color: '#94A3B8',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#FFFFFF'; e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = '#94A3B8'; e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)'; }}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Search Bar */}
                <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
                    <div style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        background: '#1F2C34',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        padding: '0 12px'
                    }}>
                        <Search size={18} color="#D4AF37" />
                        <input
                            type="text"
                            value={search}
                            onChange={handleSearchChange}
                            placeholder={rtl ? 'ابحث بالاسم أو الماركة (ساعات، مجوهرات، أزياء...)' : 'Search by name or brand (watches, jewelry...)'}
                            style={{
                                width: '100%',
                                background: 'transparent',
                                border: 'none',
                                outline: 'none',
                                color: '#FFFFFF',
                                fontSize: '13px',
                                padding: '12px 10px',
                                direction: rtl ? 'rtl' : 'ltr'
                            }}
                            autoFocus
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => { setSearch(''); fetchItems(''); }}
                                style={{ background: 'transparent', border: 'none', color: '#94A3B8', cursor: 'pointer', padding: 0 }}
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Products List */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px', minHeight: '260px', maxHeight: '55vh' }}>
                    {loading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', gap: '10px' }}>
                            <Loader2 size={30} color="#D4AF37" className="animate-spin" />
                            <span style={{ fontSize: '12px', color: '#94A3B8' }}>{rtl ? 'جاري جلب المنتجات...' : 'Fetching items...'}</span>
                        </div>
                    ) : products.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#94A3B8' }}>
                            <ShoppingBag size={40} color="#334155" style={{ margin: '0 auto 12px auto' }} />
                            <p style={{ margin: 0, fontSize: '13px', color: '#CBD5E1' }}>
                                {rtl ? 'لم يتم العثور على أي منتجات مطابقة' : 'No matching products found'}
                            </p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {products.map((p) => {
                                const img = p.images?.[0] || p.image || p.thumbnailUrl || '';
                                const isSelected = sharedId === p.id;

                                return (
                                    <div
                                        key={p.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            padding: '10px 12px',
                                            background: '#141E26',
                                            border: '1px solid rgba(255, 255, 255, 0.08)',
                                            borderRadius: '14px',
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => handleShare(p)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.5)';
                                            e.currentTarget.style.background = '#1A2732';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                                            e.currentTarget.style.background = '#141E26';
                                        }}
                                    >
                                        <div style={{
                                            width: '56px',
                                            height: '56px',
                                            borderRadius: '10px',
                                            overflow: 'hidden',
                                            background: '#070b0e',
                                            flexShrink: 0,
                                            border: '1px solid rgba(255, 255, 255, 0.1)'
                                        }}>
                                            {img ? (
                                                <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <ShoppingBag size={20} color="#D4AF37" opacity={0.5} />
                                                </div>
                                            )}
                                        </div>

                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <h4 style={{
                                                margin: '0 0 4px 0',
                                                fontSize: '13px',
                                                fontWeight: '700',
                                                color: '#FFFFFF',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {p.name || p.title}
                                            </h4>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ color: '#D4AF37', fontSize: '13px', fontWeight: '800' }}>
                                                    {p.price ? Number(p.price).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '0.00'} {p.currency || '$'}
                                                </span>
                                                {p.brand && (
                                                    <span style={{ fontSize: '11px', color: '#94A3B8' }}>
                                                        • {p.brand}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleShare(p);
                                            }}
                                            style={{
                                                background: isSelected ? '#10B981' : '#D4AF37',
                                                border: 'none',
                                                color: '#000000',
                                                borderRadius: '8px',
                                                padding: '7px 12px',
                                                fontSize: '11.5px',
                                                fontWeight: '800',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                flexShrink: 0,
                                                boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            {isSelected ? (
                                                <>
                                                    <Check size={14} />
                                                    <span>{rtl ? 'تمت المشاركة' : 'Shared'}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Send size={13} style={{ transform: rtl ? 'rotate(180deg)' : 'none' }} />
                                                    <span>{rtl ? 'مشاركة' : 'Share'}</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductShareModal;
